import { readdir, stat } from "node:fs/promises";
import { homedir, userInfo } from "node:os";
import path from "node:path";

export interface FilesystemRoot {
  /** Absolute path to navigate to. */
  path: string;
  /** Human label shown in the UI (e.g. "Home", "Macintosh HD", "C:\\"). */
  label: string;
  /**
   * Hint for the UI to pick an icon / sort group. "home" sorts first,
   * "drive" second (removable / mounted volumes), "system" last.
   */
  kind: "home" | "drive" | "system";
}

/**
 * Enumerate root locations a user might want to jump to when picking a
 * project directory. Cross-platform: each OS returns the conventional set of
 * roots a file picker would surface.
 *
 *   macOS:   ~, /, every entry of /Volumes
 *   Linux:   ~, /, every entry of /mnt and /media/<user>
 *   Windows: every available drive letter (A:\..Z:\), ~ if resolvable
 *
 * Entries that don't exist or aren't readable are silently dropped.
 */
export async function listFilesystemRoots(): Promise<FilesystemRoot[]> {
  const home = process.env.HOME ?? homedir();
  const roots: FilesystemRoot[] = [];

  if (home) {
    roots.push({ path: home, label: "Home", kind: "home" });
  }

  if (process.platform === "win32") {
    for (const drive of await enumerateWindowsDrives()) {
      roots.push({ path: drive, label: drive, kind: "drive" });
    }
    return roots;
  }

  roots.push({ path: "/", label: "/", kind: "system" });

  const mountParents =
    process.platform === "darwin"
      ? ["/Volumes"]
      : ["/mnt", `/media/${userInfo().username}`, "/media"];

  for (const parent of mountParents) {
    for (const child of await listChildDirectories(parent)) {
      roots.push({ path: child.path, label: child.name, kind: "drive" });
    }
  }

  return dedupeByPath(roots);
}

async function enumerateWindowsDrives(): Promise<string[]> {
  const drives: string[] = [];
  for (let code = "A".charCodeAt(0); code <= "Z".charCodeAt(0); code++) {
    const letter = String.fromCharCode(code);
    const drivePath = `${letter}:\\`;
    try {
      await stat(drivePath);
      drives.push(drivePath);
    } catch {
      // Drive letter not mounted — skip.
    }
  }
  return drives;
}

async function listChildDirectories(parent: string): Promise<{ name: string; path: string }[]> {
  try {
    const entries = await readdir(parent, { withFileTypes: true });
    const out: { name: string; path: string }[] = [];
    for (const entry of entries) {
      if (entry.name.startsWith(".")) continue;
      if (!entry.isDirectory() && !entry.isSymbolicLink()) continue;
      out.push({ name: entry.name, path: path.join(parent, entry.name) });
    }
    return out;
  } catch {
    return [];
  }
}

function dedupeByPath(roots: FilesystemRoot[]): FilesystemRoot[] {
  const seen = new Set<string>();
  const out: FilesystemRoot[] = [];
  for (const root of roots) {
    if (seen.has(root.path)) continue;
    seen.add(root.path);
    out.push(root);
  }
  return out;
}
